import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { isEmbedded, notifyBadgeStatus } from '../../lib/embed';
import { apiPost } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';

interface ProjectAttentionStatus {
  projectId: string;
  badgeEnabled: boolean;
  hasAttention: boolean;
  unreadTicketCount: number;
  openTicketCount: number;
  latestSupportActivityAt?: string;
  lastSeenAt?: string;
}

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const embedded = isEmbedded();
  const { status, user } = useAuth();
  const seenProjectIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!embedded || status !== 'authenticated' || !user) {
      seenProjectIdRef.current = null;
      return;
    }

    const embeddedProjectId = user.projectIds[0];
    if (!embeddedProjectId || seenProjectIdRef.current === embeddedProjectId) {
      return;
    }

    seenProjectIdRef.current = embeddedProjectId;
    let cancelled = false;

    void apiPost<ProjectAttentionStatus>(`/api/customer/projects/${embeddedProjectId}/attention/seen`, {})
      .then((res) => {
        if (!cancelled && res.success && res.data) {
          notifyBadgeStatus(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          seenProjectIdRef.current = null;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [embedded, status, user]);

  if (embedded) {
    return (
      <div className="h-screen overflow-hidden" style={{ background: 'var(--base)', color: 'var(--ink)' }}>
        <div className="grain" />
        <div className="relative z-10 flex h-full min-h-0 flex-col">
          <main className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
            <div className="mx-auto max-w-[1040px] pb-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--base)', color: 'var(--ink)' }}>
      {/* Grain overlay */}
      <div className="grain" />

      {/* Ambient glows */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div
          className="absolute -left-32 top-0 h-[500px] w-[500px] rounded-full blur-[60px]"
          style={{ background: 'radial-gradient(circle, var(--accent-glow), transparent 65%)' }}
        />
        <div
          className="absolute -right-32 bottom-0 h-[600px] w-[600px] rounded-full blur-[80px]"
          style={{ background: 'radial-gradient(circle, var(--accent-glow), transparent 60%)' }}
        />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px]">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <div className="flex min-h-0 flex-1 flex-col">
          <Header onMenuToggle={() => setSidebarOpen(true)} />

          <main className="min-h-0 flex-1 overflow-y-auto px-6 py-8 lg:px-10 lg:py-10">
            <div className="mx-auto max-w-[960px]">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
